import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { ImageUploadBox } from "../../../components/common/ImageUploadBox";
import { resolveEmployeeId } from "../../../utils/employeeIdentity";

const URGENCY_OPTIONS = [
  { value: "low", label: "낮음", color: "success" },
  { value: "medium", label: "보통", color: "warning" },
  { value: "high", label: "긴급", color: "error" },
];

const EMPTY_FORM = {
  proposer_emp_id: "",
  location_id: "",
  machine_id: "",
  location_text: "",
  machine_text: "",
  urgency: "medium",
  proposal_text: "",
};

const QUICK_TEXTS = [
  "방호덮개가 열려 있어 접촉 위험이 있습니다.",
  "바닥에 오일이 흘러 미끄러질 수 있습니다.",
  "작업 동선과 지게차 동선이 겹칩니다.",
];

const BRAND_COLOR = "#0f766e";
const BRAND_DARK = "#0b5f57";
const PAGE_BG = "#f5f7fb";
const CARD_SHADOW = "0 18px 45px rgba(15, 23, 42, 0.08)";
const DEMO_EMP_ID = "6";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: "#fff",
  },
  "& .MuiFormHelperText-root": {
    mx: 0,
  },
};

const PROPOSAL_STATUS_META = {
  pending: { label: "검토 대기", color: "warning" },
  approved: { label: "승인", color: "success" },
  rejected: { label: "거절", color: "error" },
};

const getProposalStatusMeta = (status) =>
  PROPOSAL_STATUS_META[status] || { label: status || "-", color: "default" };

const getUrgencyMeta = (urgency) =>
  URGENCY_OPTIONS.find((option) => option.value === urgency) || URGENCY_OPTIONS[1];

const getProposalDateText = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return date.toLocaleString("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildFormData = (form, photoFile) => {
  const formData = new FormData();
  formData.append("proposer_emp_id", form.proposer_emp_id);
  formData.append("proposal_text", form.proposal_text);
  formData.append("location_id", form.location_id || "");
  formData.append("machine_id", form.machine_id || "");
  formData.append("location_text", form.location_text || "");
  formData.append("machine_text", form.machine_text || "");
  formData.append("urgency", form.urgency || "medium");
  if (photoFile) formData.append("photo", photoFile);
  return formData;
};

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

function DetailLine({ label, value, children }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ py: 0.85 }}>
      <Typography
        variant="caption"
        sx={{
          width: 82,
          flex: "0 0 auto",
          color: "#64748b",
          fontWeight: 900,
          lineHeight: 1.55,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {children || (
          <Typography sx={{ color: "#0f172a", fontWeight: 800, lineHeight: 1.55, overflowWrap: "anywhere" }}>
            {value || "-"}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function ProposalHistoryPanel({ proposals = [], loading = false, onSelect }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.35, sm: 1.6, lg: 2 },
        border: "1px solid #e5e7eb",
        borderRadius: { xs: 2, lg: 3 },
        bgcolor: "#fff",
      }}
    >
      <Stack spacing={1.2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>
            내 제안 이력
          </Typography>
          <Chip
            size="small"
            label={`${proposals.length}건`}
            sx={{ bgcolor: "#f8fafc", color: "#475569", fontWeight: 900 }}
          />
        </Stack>
        {loading ? (
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 800 }}>
            접수 이력을 불러오는 중입니다.
          </Typography>
        ) : proposals.length ? (
          <Stack spacing={0.85}>
            {proposals.map((proposal) => {
              const meta = getProposalStatusMeta(proposal.status);
              return (
                <Box
                  component="button"
                  type="button"
                  key={proposal.proposal_id}
                  onClick={() => onSelect?.(proposal)}
                  sx={{
                    width: "100%",
                    appearance: "none",
                    textAlign: "left",
                    p: 1.1,
                    border: "1px solid #edf2f7",
                    borderRadius: "8px",
                    bgcolor: "#fbfdff",
                    cursor: "pointer",
                    transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.08)",
                      transform: "translateY(-1px)",
                    },
                    "&:focus-visible": {
                      outline: "2px solid #4f46e5",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        minWidth: 0,
                        fontWeight: 900,
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {proposal.proposal_text}
                    </Typography>
                    <Chip
                      size="small"
                      color={meta.color}
                      label={meta.label}
                      sx={{ fontWeight: 900, flex: "0 0 auto" }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.45,
                      color: "#64748b",
                      fontWeight: 800,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getProposalDateText(proposal.created_at)}
                    {proposal.reject_reason ? ` · ${proposal.reject_reason}` : ""}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 800 }}>
            아직 접수한 제안이 없습니다.
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

export function WorkerHazardProposal() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.auth.user);
  const queryEmpId = searchParams.get("emp_id") || "";
  const queryLocationId = searchParams.get("location_id") || "";
  const queryMachineId = searchParams.get("machine_id") || "";
  const queryLocation = searchParams.get("location") || "";
  const queryMachine = searchParams.get("machine") || "";
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    proposer_emp_id: queryEmpId,
    location_id: queryLocationId,
    machine_id: queryMachineId,
    location_text: queryLocation,
    machine_text: queryMachine,
  }));
  const [photoFile, setPhotoFile] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [selectedHistoryProposal, setSelectedHistoryProposal] = useState(null);
  const proposalInputRef = useRef(null);
  const speechSupport = useMemo(() => {
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isAppleMobile = /iPad|iPhone|iPod/i.test(userAgent);
    const supportsBrowserSpeech =
      typeof window !== "undefined" && Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

    if (isAppleMobile && !supportsBrowserSpeech) {
      return {
        supported: false,
        title: "iPhone/iPad 음성 입력",
        description: "버튼을 누르면 입력칸이 열립니다. 키보드 하단의 마이크 버튼을 눌러 말하면 내용이 입력됩니다.",
      };
    }

    if (!supportsBrowserSpeech) {
      return {
        supported: false,
        title: "기기 키보드 음성 입력",
        description: "브라우저 직접 음성인식은 지원되지 않습니다. 입력칸을 연 뒤 키보드의 마이크 입력을 사용해주세요.",
      };
    }

    return {
      supported: true,
      title: "브라우저 음성 입력",
      description: "버튼을 누른 뒤 현장의 위험 요인을 말하면 입력칸에 자동으로 추가됩니다.",
    };
  }, []);

  const optionsQuery = useQuery({
    queryKey: ["worker-hazard-proposal-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/worker-hazard-proposals/options");
      return response.data || { employees: [], locations: [], machines: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [], locations: [], machines: [] },
  });

  const employees = useMemo(() => optionsQuery.data?.employees || [], [optionsQuery.data]);
  const locations = useMemo(() => optionsQuery.data?.locations || [], [optionsQuery.data]);
  const machines = useMemo(() => optionsQuery.data?.machines || [], [optionsQuery.data]);
  const selectedLocationId = form.location_id ? Number(form.location_id) : null;
  const selectedLocation = useMemo(
    () => locations.find((location) => Number(location.location_id) === selectedLocationId) || null,
    [locations, selectedLocationId]
  );
  const filteredMachines = useMemo(() => {
    if (!selectedLocationId) return machines;
    return machines.filter((machine) =>
      Array.isArray(machine.location_ids) && machine.location_ids.includes(selectedLocationId)
    );
  }, [machines, selectedLocationId]);

  const resolvedProposerEmpId = useMemo(() => {
    return resolveEmployeeId({ user, queryEmpId, employees }) || DEMO_EMP_ID;
  }, [employees, queryEmpId, user]);

  useEffect(() => {
    if (!resolvedProposerEmpId) return;
    setForm((prev) =>
      prev.proposer_emp_id === resolvedProposerEmpId
        ? prev
        : { ...prev, proposer_emp_id: resolvedProposerEmpId }
    );
  }, [resolvedProposerEmpId]);

  const myProposalsQuery = useQuery({
    queryKey: ["worker-hazard-proposals", group_code, project_code, "mine", resolvedProposerEmpId],
    queryFn: async () => {
      const response = await api.get("/worker-hazard-proposals");
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && resolvedProposerEmpId),
    placeholderData: [],
  });

  const myProposals = useMemo(() => {
    return (myProposalsQuery.data || [])
      .filter((proposal) => String(proposal.proposer_emp_id) === String(resolvedProposerEmpId))
      .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
      .slice(0, 5);
  }, [myProposalsQuery.data, resolvedProposerEmpId]);

  const completion = useMemo(() => {
    const checks = [
      Boolean(form.proposer_emp_id),
      Boolean(form.proposal_text.trim()),
      Boolean(form.urgency),
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [form.proposer_emp_id, form.proposal_text, form.urgency]);

  const isSubmitReady = Boolean(form.proposer_emp_id && form.proposal_text.trim());

  const createMutation = useMutation({
    mutationFn: async () => api.post("/worker-hazard-proposals", buildFormData(form, photoFile)),
    onSuccess: (response) => {
      setSubmitted(response.data?.data || {});
      setForm({
        ...EMPTY_FORM,
        proposer_emp_id: resolvedProposerEmpId || queryEmpId,
      });
      setPhotoFile(null);
      myProposalsQuery.refetch();
      toast.success("유해·위험요인 제안이 접수되었습니다. 입력칸을 비웠습니다.");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "유해·위험요인 제안 접수 중 오류가 발생했습니다.");
    },
  });
  const submitDisabledReason = createMutation.isPending
    ? "제안을 접수하는 중입니다."
    : !form.proposer_emp_id
    ? "직원 정보가 확인되면 제출할 수 있습니다."
    : !form.proposal_text.trim()
    ? "위험 요인 내용을 입력하세요."
    : "";

  const updateForm = (key, value) => {
    setSubmitted(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateLocation = (value) => {
    setSubmitted(null);
    setForm((prev) => ({ ...prev, location_id: value, machine_id: "" }));
  };

  useEffect(() => {
    if (!form.machine_id || !selectedLocationId) return;
    const machineStillVisible = filteredMachines.some(
      (machine) => String(machine.machine_id) === String(form.machine_id)
    );
    if (!machineStillVisible) {
      setForm((prev) => ({ ...prev, machine_id: "" }));
    }
  }, [filteredMachines, form.machine_id, selectedLocationId]);

  const appendQuickText = (text) => {
    updateForm("proposal_text", form.proposal_text ? `${form.proposal_text}\n${text}` : text);
  };

  const startVoiceInput = () => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      proposalInputRef.current?.focus();
      toast.info(speechSupport.description);
      return;
    }

    const recognition = new Recognition();
    recognition.lang = "ko-KR";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || "";
      if (transcript) appendQuickText(transcript);
    };
    recognition.onerror = () => {
      toast.warning("음성을 인식하지 못했습니다. 다시 시도해주세요.");
    };
    recognition.onend = () => {
      setIsListening(false);
    };
    recognition.start();
  };

  const handleSubmit = () => {
    if (!form.proposer_emp_id) {
      toast[optionsQuery.isLoading ? "info" : "warning"](
        optionsQuery.isLoading
          ? "로그인 계정의 직원 정보를 확인하는 중입니다."
          : "로그인 계정과 직원 정보가 연결되지 않아 제출할 수 없습니다."
      );
      return;
    }
    if (!form.proposal_text.trim()) {
      toast.warning("위험 요인 내용을 입력해주세요.");
      return;
    }
    createMutation.mutate();
  };

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
      proposer_emp_id: resolvedProposerEmpId || queryEmpId,
    });
    setPhotoFile(null);
    setSubmitted(null);
  };

  const confirmReset = () => {
    resetForm();
    setResetConfirmOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        overflow: "auto",
        overflowX: "hidden",
        bgcolor: PAGE_BG,
        px: { xs: 1.25, sm: 2.25, md: 3 },
        py: { xs: 1.25, sm: 2.25, md: 3 },
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      <Grid
        container
        rowSpacing={2}
        columnSpacing={{ xs: 0, lg: 2.5 }}
        justifyContent="center"
        sx={{
          width: "100%",
          maxWidth: { xs: 620, sm: 760, lg: 1180, xl: 1320 },
          mx: "auto",
          my: 0,
          boxSizing: "border-box",
        }}
      >
        <Grid item xs={12} lg={7} xl={8}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: { xs: 560, sm: 720, lg: "none" },
              mx: "auto",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: { xs: 2, sm: 3 },
              bgcolor: "#fff",
              boxShadow: { xs: "0 8px 24px rgba(15, 23, 42, 0.06)", sm: CARD_SHADOW },
            }}
          >
            <Box
              sx={{
                p: { xs: 2.25, sm: 3 },
                position: { xs: "sticky", sm: "static" },
                top: 0,
                zIndex: 2,
                bgcolor: "#fff",
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "flex-start" }}
                spacing={1.5}
              >
                <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: `${BRAND_COLOR}1a`,
                      color: BRAND_COLOR,
                      flex: "0 0 auto",
                    }}
                  >
                    <QrCodeScannerIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a", letterSpacing: 0, fontSize: { xs: 20, sm: 24 } }}>
                      근로자 유해·위험요인 제안
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<RestartAltIcon />}
                  onClick={() => setResetConfirmOpen(true)}
                  disabled={createMutation.isPending}
                  sx={{
                    alignSelf: { xs: "flex-end", sm: "flex-start" },
                    borderRadius: 2,
                    px: 1.25,
                    fontWeight: 900,
                    bgcolor: "#fff",
                    borderColor: "#dbe3ef",
                    color: "#475569",
                    whiteSpace: "nowrap",
                  }}
                >
                  초기화
                </Button>
              </Stack>
            </Box>

            <Stack spacing={isPhone ? 1.75 : 2.15} sx={{ p: { xs: 1.75, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
              <Alert
                icon={<MyLocationIcon />}
                severity="info"
                sx={{
                  alignItems: "center",
                  borderRadius: 2,
                  bgcolor: "#eff6ff",
                  color: "#1e3a8a",
                  "& .MuiAlert-icon": { color: "#2563eb" },
                  "& .MuiAlert-message": {
                    minWidth: 0,
                    overflowWrap: "anywhere",
                    wordBreak: "keep-all",
                    lineHeight: 1.55,
                  },
                }}
              >
                장소는 기준정보에서 선택하고, 정확하지 않으면 공란 또는 보충 메모로 제출할 수 있습니다.
              </Alert>

              {!resolvedProposerEmpId && !optionsQuery.isLoading ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  로그인 계정에 직원 정보가 연결되지 않아 제안을 제출할 수 없습니다. 계정의 직원 정보를 연결한 뒤 다시 시도해주세요.
                </Alert>
              ) : null}

              <Grid
                container
                rowSpacing={2}
                columnSpacing={{ xs: 0, md: 1.5 }}
                sx={{ width: "100%", mx: 0, my: 0 }}
              >
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>장소/라인 선택</InputLabel>
                    <Select
                      label="장소/라인 선택"
                      value={form.location_id || ""}
                      onChange={(event) => updateLocation(event.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <LocationOnIcon fontSize="small" />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="">선택 안 함</MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location.location_id} value={String(location.location_id)}>
                          {location.location_path || location.location_name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {selectedLocation
                        ? selectedLocation.location_path || selectedLocation.location_name
                        : "정확하지 않으면 선택하지 않고 보충 메모만 남겨도 됩니다."}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>설비/작업대상 선택</InputLabel>
                    <Select
                      label="설비/작업대상 선택"
                      value={form.machine_id || ""}
                      onChange={(event) => updateForm("machine_id", event.target.value)}
                    >
                      <MenuItem value="">선택 안 함</MenuItem>
                      {filteredMachines.map((machine) => (
                        <MenuItem key={machine.machine_id} value={String(machine.machine_id)}>
                          {machine.display_name || machine.safe_machine_name || machine.machine_name || machine.machine_manage_code}
                        </MenuItem>
                      ))}
                      {selectedLocationId && filteredMachines.length === 0 ? (
                        <MenuItem value="" disabled>
                          선택한 장소에 배정된 설비가 없습니다
                        </MenuItem>
                      ) : null}
                    </Select>
                    <FormHelperText>
                      {selectedLocationId
                        ? "선택한 장소에 배정된 설비만 표시됩니다."
                        : "장소를 선택하면 연결된 설비만 표시됩니다."}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="장소 보충 메모"
                    value={form.location_text}
                    onChange={(event) => updateForm("location_text", event.target.value)}
                    placeholder="예: 2층 창고 앞, 출입문 근처"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="설비 보충 메모"
                    value={form.machine_text}
                    onChange={(event) => updateForm("machine_text", event.target.value)}
                    placeholder="예: 지게차 통로, 임시 작업대"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth sx={fieldSx}>
                    <InputLabel>긴급도</InputLabel>
                    <Select
                      label="긴급도"
                      value={form.urgency}
                      onChange={(event) => updateForm("urgency", event.target.value)}
                    >
                      {URGENCY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    inputRef={proposalInputRef}
                    fullWidth
                    required
                    multiline
                    minRows={5}
                    label="위험 요인을 입력하세요"
                    placeholder="예: 프레스 하부에 오일이 고여 미끄러짐 위험이 있습니다."
                    value={form.proposal_text}
                    onChange={(event) => updateForm("proposal_text", event.target.value)}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant={isListening ? "contained" : "outlined"}
                    color={isListening ? "success" : speechSupport.supported ? "primary" : "inherit"}
                    startIcon={
                      isListening ? (
                        <Box
                          component="span"
                          sx={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            "@keyframes voiceWave": {
                              "0%": { transform: "scale(0.78)", opacity: 0.42 },
                              "70%": { opacity: 0.16 },
                              "100%": { transform: "scale(1.65)", opacity: 0 },
                            },
                            "&::before, &::after": {
                              content: '""',
                              position: "absolute",
                              inset: 2,
                              borderRadius: "50%",
                              border: "1px solid rgba(255,255,255,0.78)",
                              animation: "voiceWave 1.8s ease-out infinite",
                            },
                            "&::after": {
                              animationDelay: "0.65s",
                            },
                          }}
                        >
                          <KeyboardVoiceIcon sx={{ position: "relative", zIndex: 1, fontSize: 20 }} />
                        </Box>
                      ) : (
                        <KeyboardVoiceIcon />
                      )
                    }
                    onClick={startVoiceInput}
                    disabled={createMutation.isPending}
                    sx={{
                      borderRadius: 2,
                      py: { xs: 1.25, sm: 1.15 },
                      fontWeight: 900,
                      minHeight: 48,
                      bgcolor: isListening ? "#0f766e" : speechSupport.supported ? "#fff" : "#f8fafc",
                      borderColor: isListening ? "#0f766e" : speechSupport.supported ? "#0f766e" : "#cbd5e1",
                      color: isListening ? "#fff" : speechSupport.supported ? BRAND_COLOR : "#475569",
                      boxShadow: isListening ? "0 10px 24px rgba(15, 118, 110, 0.2)" : "none",
                      "&:hover": {
                        bgcolor: isListening ? "#0d9488" : speechSupport.supported ? "#f0fdfa" : "#f1f5f9",
                        borderColor: isListening ? "#0d9488" : speechSupport.supported ? BRAND_COLOR : "#94a3b8",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {isListening ? "음성 듣는 중" : speechSupport.supported ? "음성으로 입력" : "키보드 마이크로 입력"}
                  </Button>
                  {isListening ? (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.75,
                        px: 0.5,
                        color: "#0f766e",
                        fontWeight: 800,
                        lineHeight: 1.45,
                        textAlign: "center",
                      }}
                    >
                      말씀하시면 입력칸에 자동으로 추가됩니다.
                    </Typography>
                  ) : null}
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "flex-start",
                      p: { xs: 1.25, sm: 1.45 },
                      borderRadius: 2,
                      bgcolor: speechSupport.supported ? "#f0fdfa" : "#f8fafc",
                      border: "1px solid",
                      borderColor: speechSupport.supported ? "#99f6e4" : "#e2e8f0",
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        bgcolor: speechSupport.supported ? `${BRAND_COLOR}1a` : "#eef2ff",
                        color: speechSupport.supported ? BRAND_COLOR : "#4f46e5",
                        flex: "0 0 auto",
                      }}
                    >
                      <KeyboardVoiceIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 13.5, lineHeight: 1.25 }}>
                        {speechSupport.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.35,
                          color: "#475569",
                          fontWeight: 700,
                          lineHeight: 1.5,
                          wordBreak: "keep-all",
                        }}
                      >
                        {speechSupport.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ display: "grid", gap: 0.85 }}>
                {QUICK_TEXTS.map((text) => (
                  <Chip
                    key={text}
                    icon={<TipsAndUpdatesIcon />}
                    label={text}
                    onClick={() => appendQuickText(text)}
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      height: "auto",
                      py: 0.75,
                      justifyContent: "flex-start",
                      bgcolor: "#f8fafc",
                      border: "1px solid",
                      borderColor: "#e2e8f0",
                      borderRadius: 2,
                      "& .MuiChip-label": { whiteSpace: "normal", fontWeight: 700 },
                    }}
                  />
                ))}
              </Box>

              <ImageUploadBox
                label="현장 사진"
                file={photoFile}
                onFileChange={setPhotoFile}
                capture="environment"
                compact
                helperText="사진은 선택 사항이며, 큰 이미지는 자동으로 줄어듭니다."
              />

              <Stack
                direction="row"
                spacing={1.25}
                sx={{
                  position: { xs: "sticky", sm: "static" },
                  bottom: 0,
                  zIndex: 1,
                  bgcolor: "#fff",
                  pt: { xs: 1.25, sm: 0 },
                  pb: { xs: "max(10px, env(safe-area-inset-bottom))", sm: 0 },
                  borderTop: { xs: "1px solid #eef2f7", sm: "none" },
                  boxShadow: { xs: "0 -10px 24px rgba(15, 23, 42, 0.06)", sm: "none" },
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={createMutation.isPending || !isSubmitReady}
                  sx={{
                    borderRadius: 2,
                    py: 1.15,
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: BRAND_COLOR,
                    boxShadow: "none",
                    "&:hover": { bgcolor: BRAND_DARK, boxShadow: "none" },
                  }}
                >
                  {createMutation.isPending ? "접수 중" : "제안 등록하기"}
                </Button>
              </Stack>
              {submitDisabledReason ? (
                <Typography
                  variant="caption"
                  sx={{
                    mt: -0.75,
                    display: "block",
                    color: "#64748b",
                    fontWeight: 800,
                    textAlign: "center",
                  }}
                >
                  {submitDisabledReason}
                </Typography>
              ) : null}

              {submitted ? (
                <Alert icon={<CheckCircleIcon />} severity="success" sx={{ borderRadius: 2 }}>
                  제안 #{submitted.proposal_id}번으로 접수되었습니다. 관리자가 검토한 뒤 승인 또는 거절 처리합니다.
                </Alert>
              ) : null}

              <Box sx={{ display: { xs: "block", lg: "none" } }}>
                <ProposalHistoryPanel
                  proposals={myProposals}
                  loading={myProposalsQuery.isLoading}
                  onSelect={setSelectedHistoryProposal}
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5} xl={4} sx={{ display: { xs: "none", lg: "block" } }}>
          <Stack
            spacing={2}
            sx={{
              position: "sticky",
              top: 24,
              maxHeight: "calc(100dvh - 48px)",
              overflow: "auto",
              pr: 0.25,
            }}
          >
            <ProposalHistoryPanel
              proposals={myProposals}
              loading={myProposalsQuery.isLoading}
              onSelect={setSelectedHistoryProposal}
            />

            <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "#fff" }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                현장 제안 흐름
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
                근로자는 자유롭게 위험을 남기고, 관리자는 검토 화면에서 장소, 설비, 위험분류를 정리한 뒤 공식 유해·위험요인으로 승인합니다.
              </Typography>
              <Divider sx={{ my: 2 }} />
              {["현장 발견", "자유 입력", "사진/음성 첨부", "관리자 검토"].map((step, index) => (
                <Stack key={step} direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: index === 0 ? "#14532d" : "#e2e8f0", color: index === 0 ? "#fff" : "#334155" }}>
                    {index + 1}
                  </Avatar>
                  <Typography sx={{ fontWeight: 800 }}>{step}</Typography>
                </Stack>
              ))}
            </Paper>

            <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3, bgcolor: "#fff" }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                입력 완성도
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Box sx={{ height: 10, borderRadius: 999, bgcolor: "#e2e8f0", overflow: "hidden" }}>
                  <Box
                    sx={{
                      width: `${completion}%`,
                      height: "100%",
                      bgcolor: completion >= 80 ? "#16a34a" : "#2563eb",
                      transition: "width 180ms ease",
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  필수 정보 기준 {completion}% 완료
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>입력 내용을 초기화할까요?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            작성 중인 제안 내용, 장소/설비 입력값, 첨부 사진이 모두 지워집니다.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setResetConfirmOpen(false)}>
            취소
          </Button>
          <Button variant="contained" color="warning" onClick={confirmReset}>
            초기화
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(selectedHistoryProposal)}
        onClose={() => setSelectedHistoryProposal(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={isPhone}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, color: "#0f172a", lineHeight: 1.25 }}>
                제안 상세
              </Typography>
            </Box>
            {selectedHistoryProposal ? (
              <Chip
                size="small"
                color={getProposalStatusMeta(selectedHistoryProposal.status).color}
                label={getProposalStatusMeta(selectedHistoryProposal.status).label}
                sx={{ fontWeight: 900, flex: "0 0 auto" }}
              />
            ) : null}
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2.25, bgcolor: "#f8fafc" }}>
          {selectedHistoryProposal ? (
            <Stack spacing={1.25}>
              {selectedHistoryProposal.photo_url ? (
                <Box
                  component="a"
                  href={resolvePhotoUrl(selectedHistoryProposal.photo_url)}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: { xs: 220, sm: 240 },
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "#f8fafc",
                    textDecoration: "none",
                  }}
                >
                  <Box
                    component="img"
                    src={resolvePhotoUrl(selectedHistoryProposal.photo_url)}
                    alt="첨부 사진"
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : null}

              <Paper elevation={0} sx={{ p: 1.6, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 900 }}>
                  제안 내용
                </Typography>
                <Typography
                  sx={{
                    mt: 0.65,
                    color: "#0f172a",
                    fontWeight: 800,
                    lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                    wordBreak: "keep-all",
                    overflowWrap: "anywhere",
                  }}
                >
                  {selectedHistoryProposal.proposal_text || "-"}
                </Typography>
              </Paper>

              <Paper elevation={0} sx={{ p: 1.45, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                <DetailLine label="접수일" value={getProposalDateText(selectedHistoryProposal.created_at)} />
                <Divider />
                <DetailLine label="긴급도">
                  <Chip
                    size="small"
                    color={getUrgencyMeta(selectedHistoryProposal.urgency).color}
                    label={getUrgencyMeta(selectedHistoryProposal.urgency).label}
                    sx={{ fontWeight: 900 }}
                  />
                </DetailLine>
                <Divider />
                <DetailLine
                  label="장소"
                  value={
                    selectedHistoryProposal.location_path ||
                    selectedHistoryProposal.location_name ||
                    selectedHistoryProposal.location_text ||
                    "-"
                  }
                />
                <Divider />
                <DetailLine
                  label="설비"
                  value={
                    selectedHistoryProposal.machine_name ||
                    selectedHistoryProposal.safe_machine_name ||
                    selectedHistoryProposal.machine_text ||
                    "-"
                  }
                />
              </Paper>

              {selectedHistoryProposal.reject_reason ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {selectedHistoryProposal.reject_reason}
                </Alert>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2.25, py: 1.5, borderTop: "1px solid #e5e7eb", bgcolor: "#fff" }}>
          <Button
            fullWidth={isPhone}
            variant="contained"
            onClick={() => setSelectedHistoryProposal(null)}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              bgcolor: BRAND_COLOR,
              boxShadow: "none",
              "&:hover": { bgcolor: BRAND_DARK, boxShadow: "none" },
            }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
